import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Airtable from "airtable";
import Notification from "../Notification.js";
import {
  isAnswer,
  isNumbered,
  numberingCompare,
  setShowNotif,
  setContents,
  removeQueryParameters,
} from "../helpers.js";
import { debugLog } from "../utils/debugLog.js";
import { OPPORTUNITIES_TABLE } from "../utils/opportunityFilterFormulas.js";
import { COMPANY_PROFILE_SEARCH_FOCUS_EVENT, consumePendingCompanyProfileSearchFocus } from "../utils/companyProfileSearch.js";

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const answersTableId = process.env.REACT_APP_AIR_ANSWERS;
const base = new Airtable({ apiKey }).base(baseId);

const MATCHING_MODES = [
  { id: "interestedIn", label: "InterestedIn" },
  { id: "allGrants", label: "All Grants" },
];

const parseInterestedGrantNames = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(String).map((s) => s.trim()).filter(Boolean);
  return String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const escapeAirtableString = (value) =>
  String(value || "").replace(/'/g, "\\'");

const grantIdentifierFor = (grant) =>
  `${grant?.fields?.Name}-${removeQueryParameters(grant?.fields?.GrantUrl)}`;

/**
 * Grant Questions & Answers — left grant tabs, one grant panel at a time.
 * Matching modes: InterestedIn (company field) or All Grants (ALLGRANTS opportunities).
 */
const CompanyGrantQuestionsSection = ({ company, className = "" }) => {
  const [answers, setAnswers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [interestedInGrants, setInterestedInGrants] = useState([]);
  const [allOpportunityGrants, setAllOpportunityGrants] = useState([]);
  const [opportunityCountsByGrantId, setOpportunityCountsByGrantId] = useState({});
  const [matchingMode, setMatchingMode] = useState("interestedIn");
  const [loading, setLoading] = useState(true);
  const [selectedGrantId, setSelectedGrantId] = useState(null);
  const [questionAnswerSearchValue, setQuestionAnswerSearchValue] = useState("");
  const [showSubmitNotif, setShowSubmitNotif] = useState({
    showNotif: false,
    contents: { type: "", heading: "", message: "" },
  });
  const pendingGrantIdFromSearchRef = useRef(null);

  const companyId = company?.id;
  const interestedKey = parseInterestedGrantNames(company?.fields?.InterestedIn).join("|");

  const grants =
    matchingMode === "allGrants" ? allOpportunityGrants : interestedInGrants;

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      setInterestedInGrants([]);
      setAllOpportunityGrants([]);
      setOpportunityCountsByGrantId({});
      setSelectedGrantId(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const interested = new Set(interestedKey.split("|").filter(Boolean));

    Promise.all([
      base("answers").select({ view: "Grid view" }).all(),
      base("questions").select({ view: "Grid view" }).all(),
      base("deduped_grants").select({ view: "Grid view" }).all(),
      base(OPPORTUNITIES_TABLE)
        .select({
          filterByFormula: `{CompanyID} = '${escapeAirtableString(companyId)}'`,
          fields: ["GrantID", "UpgradeName", "CompanyID"],
        })
        .all()
        .catch((err) => {
          console.error(
            "[CompanyGrantQuestionsSection] Error loading ALLGRANTS opportunities:",
            err
          );
          return [];
        }),
    ])
      .then(([answerRecords, questionRecords, grantRecords, opportunityRecords]) => {
        if (cancelled) return;
        setAnswers(answerRecords);
        setQuestions(questionRecords);

        const interestedMatched = grantRecords.filter(
          (record) => record.fields.Name && interested.has(record.fields.Name)
        );
        setInterestedInGrants(interestedMatched);

        const grantIdCounts = {};
        for (const opp of opportunityRecords || []) {
          const rawGrantId = opp.fields?.GrantID;
          const grantId = Array.isArray(rawGrantId)
            ? rawGrantId[0]
            : rawGrantId
              ? String(rawGrantId).trim()
              : null;
          if (!grantId) continue;
          grantIdCounts[grantId] = (grantIdCounts[grantId] || 0) + 1;
        }
        setOpportunityCountsByGrantId(grantIdCounts);

        const opportunityGrantIds = Object.keys(grantIdCounts);
        const grantsById = new Map(grantRecords.map((g) => [g.id, g]));
        const opportunityMatched = opportunityGrantIds
          .map((id) => grantsById.get(id))
          .filter((record) => record?.fields?.Name)
          .sort((a, b) =>
            String(a.fields.Name).localeCompare(String(b.fields.Name), undefined, {
              sensitivity: "base",
            })
          );
        setAllOpportunityGrants(opportunityMatched);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[CompanyGrantQuestionsSection] Error loading Q&A data:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, interestedKey]);

  // Keep selected grant valid for the active matching mode.
  useEffect(() => {
    if (grants.length === 0) {
      setSelectedGrantId(null);
      return;
    }
    setSelectedGrantId((prev) => {
      const preferred = pendingGrantIdFromSearchRef.current;
      if (preferred && grants.some((g) => g.id === preferred)) {
        pendingGrantIdFromSearchRef.current = null;
        return preferred;
      }
      if (prev && grants.some((g) => g.id === prev)) return prev;
      return grants[0].id;
    });
  }, [grants, matchingMode]);

  // Focus from company profile search palette (⌘⇧K)
  useEffect(() => {
    const applyFocus = (detail) => {
      if (!detail) return;
      if (detail.matchingMode === "interestedIn" || detail.matchingMode === "allGrants") {
        setMatchingMode(detail.matchingMode);
      }
      if (detail.grantId) {
        pendingGrantIdFromSearchRef.current = detail.grantId;
        setSelectedGrantId(detail.grantId);
      }
      if (typeof detail.searchQuery === "string") {
        setQuestionAnswerSearchValue(detail.searchQuery);
      } else if (detail.questionText) {
        setQuestionAnswerSearchValue(String(detail.questionText));
      }
    };

    applyFocus(consumePendingCompanyProfileSearchFocus());

    const handleFocus = (event) => {
      applyFocus(event?.detail);
      consumePendingCompanyProfileSearchFocus();
    };

    window.addEventListener(COMPANY_PROFILE_SEARCH_FOCUS_EVENT, handleFocus);
    return () =>
      window.removeEventListener(COMPANY_PROFILE_SEARCH_FOCUS_EVENT, handleFocus);
  }, []);

  const sortQuestions = useCallback((questionsArray) => {
    return [...questionsArray].sort((a, b) => {
      if (a.fields.Link && b.fields.Link) return 0;
      if (a.fields.Link) return -1;
      if (b.fields.Link) return 1;

      if (!isNumbered(a?.fields?.QuestionText) && isAnswer(a.answer)) return 1;
      if (!isNumbered(b?.fields?.QuestionText) && isAnswer(b.answer)) return -1;

      if (isNumbered(a?.fields?.QuestionText) && isNumbered(b?.fields?.QuestionText)) {
        return numberingCompare(a.fields.QuestionText, b.fields.QuestionText);
      }
      if (isNumbered(a?.fields?.QuestionText)) return 1;
      if (isNumbered(b?.fields?.QuestionText)) return -1;

      return 0;
    });
  }, []);

  const buildQuestionsForGrant = useCallback(
    (grant, { applySearch = true } = {}) => {
      if (!grant || !companyId) return [];
      const grantIdentifier = grantIdentifierFor(grant);
      let matched = questions.filter(
        (question) =>
          question.fields.grantIdentifier &&
          question.fields.grantIdentifier.split(",").includes(grantIdentifier)
      );

      matched = matched.map((question) => {
        const potentialAnswers = answers.filter(
          (ans) =>
            Boolean(ans.fields.AnswerText) &&
            ans.fields.QuestionID === question.fields.QuestionID &&
            ans.fields.grantIdentifier === grantIdentifier &&
            ans.fields.CompanyID === companyId
        );
        return {
          ...question,
          answer: potentialAnswers[0] || null,
        };
      });

      matched = sortQuestions(matched);

      if (applySearch && questionAnswerSearchValue) {
        const q = questionAnswerSearchValue.toLowerCase();
        matched = matched.filter(
          (question) =>
            question?.fields?.QuestionText?.toLowerCase().includes(q) ||
            (isAnswer(question.answer) &&
              question.answer.fields.AnswerText.toLowerCase().includes(q))
        );
      }

      return matched;
    },
    [questions, answers, companyId, sortQuestions, questionAnswerSearchValue]
  );

  const searchQuery = questionAnswerSearchValue.trim();
  const isSearching = Boolean(searchQuery);

  const grantTabs = useMemo(() => {
    return grants.map((grant) => {
      const allQuestions = buildQuestionsForGrant(grant, { applySearch: false });
      const matchedQuestions = buildQuestionsForGrant(grant, { applySearch: true });
      const answered = allQuestions.filter((q) => Boolean(q.answer)).length;
      const unanswered = allQuestions.length - answered;
      return {
        grant,
        total: allQuestions.length,
        answered,
        unanswered,
        matchCount: matchedQuestions.length,
        matchedQuestions,
      };
    });
  }, [grants, buildQuestionsForGrant]);

  const selectedGrant = grants.find((g) => g.id === selectedGrantId) || null;
  const selectedQuestions = selectedGrant
    ? buildQuestionsForGrant(selectedGrant, { applySearch: true })
    : [];

  const crossGrantSearchResults = useMemo(() => {
    if (!isSearching) return [];
    return grantTabs
      .filter((tab) => tab.matchCount > 0)
      .map((tab) => ({
        grant: tab.grant,
        questions: tab.matchedQuestions,
      }));
  }, [isSearching, grantTabs]);

  const totalSearchMatches = useMemo(
    () => crossGrantSearchResults.reduce((sum, group) => sum + group.questions.length, 0),
    [crossGrantSearchResults]
  );

  // When searching, prefer a tab that has matches if the current tab has none.
  useEffect(() => {
    if (!isSearching || !selectedGrantId) return;
    const current = grantTabs.find((tab) => tab.grant.id === selectedGrantId);
    if (current && current.matchCount > 0) return;
    const firstMatch = grantTabs.find((tab) => tab.matchCount > 0);
    if (firstMatch) setSelectedGrantId(firstMatch.grant.id);
  }, [isSearching, selectedGrantId, grantTabs]);

  const renderQuestionRow = (grant, question) => {
    const inputId = `${grant.id},${question.id}`;
    return (
      <li key={`${grant.id}-${question.id}`} className="py-4 first:pt-0 last:pb-0 min-w-0">
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-900 break-words"
        >
          {question.fields.QuestionText}
        </label>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-stretch min-w-0">
          <input
            type="text"
            name={inputId}
            id={inputId}
            key={`${inputId}-${question.answer?.id || "new"}-${question.answer?.fields?.AnswerText || ""}`}
            defaultValue={question.answer?.fields?.AnswerText || ""}
            className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors"
            placeholder="Enter answer"
          />
          <button
            type="button"
            onClick={async () => {
              const el = document.getElementById(inputId);
              const newAnswerText = el?.value ?? "";
              await submitAnswer(
                company.id,
                question.fields.QuestionID,
                grantIdentifierFor(grant),
                question.answer,
                newAnswerText
              );
            }}
            className="inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors sm:w-28"
          >
            {question.answer ? "Update" : "Submit"}
          </button>
        </div>
      </li>
    );
  };

  const refreshAnswers = async () => {
    const records = await base("answers").select({ view: "Grid view" }).all();
    setAnswers(records);
  };

  const submitAnswer = async (companyRecordId, questionId, grantIdentifier, answer, newAnswerText) => {
    const contents = { type: "", heading: "", message: "" };

    try {
      if (isAnswer(answer) && Boolean(answer.id)) {
        debugLog("running patch");
        const response = await fetch(
          `https://api.airtable.com/v0/${baseId}/${answersTableId}/${answer.id}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fields: { AnswerText: newAnswerText },
            }),
          }
        );
        if (!response.ok) throw new Error(JSON.stringify(response));
        const data = await response.json();
        if (!data || JSON.stringify(data).toLowerCase().includes("error")) {
          throw data;
        }
        contents.type = "success";
        contents.message = `Answer updated to ${newAnswerText}`;
        contents.heading = "Answer Successfully Edited";
      } else {
        debugLog("running post");
        const response = await fetch(
          `https://api.airtable.com/v0/${baseId}/${answersTableId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${apiKey}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              fields: {
                AnswerText: newAnswerText,
                CompanyID: companyRecordId,
                grantIdentifier,
                QuestionID: questionId,
                AnswerID: `${Date.now()}`,
              },
            }),
          }
        );
        const data = await response.json();
        if (!data || JSON.stringify(data).toLowerCase().includes("error")) {
          throw data;
        }
        contents.type = "success";
        contents.message = `Answered '${newAnswerText}'`;
        contents.heading = "Answer Successfully Submitted!";
      }

      await refreshAnswers();
      setContents(contents, setShowSubmitNotif);
      setShowNotif(true, setShowSubmitNotif);
    } catch (error) {
      console.error("Error submitting answer:", error);
      contents.type = "error";
      contents.message = typeof error === "string" ? error : JSON.stringify(error);
      contents.heading = "Failed to submit";
      setContents(contents, setShowSubmitNotif);
      setShowNotif(true, setShowSubmitNotif);
    }
  };

  if (!companyId) return null;

  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold leading-7 text-gray-900">
            Grant Questions and Answers
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            {loading
              ? "Loading grants…"
              : isSearching
                ? `${totalSearchMatches} match${totalSearchMatches === 1 ? "" : "es"} across ${
                    crossGrantSearchResults.length
                  } grant${crossGrantSearchResults.length === 1 ? "" : "s"}`
                : matchingMode === "allGrants"
                  ? `${grants.length} grant${grants.length === 1 ? "" : "s"} from ALLGRANTS opportunities`
                  : `${grants.length} grant${grants.length === 1 ? "" : "s"} from InterestedIn`}
          </p>
        </div>
        <input
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 transition-colors sm:max-w-xs"
          onChange={(e) => setQuestionAnswerSearchValue(e.target.value)}
          value={questionAnswerSearchValue}
          placeholder="Search all grants’ questions or answers"
        />
      </div>

      <div className="mt-4 inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {MATCHING_MODES.map((mode) => {
          const isActive = matchingMode === mode.id;
          const count =
            mode.id === "allGrants"
              ? allOpportunityGrants.length
              : interestedInGrants.length;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => setMatchingMode(mode.id)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-gray-900 text-white"
                  : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              {mode.label}
              <span className={`ml-1.5 tabular-nums ${isActive ? "text-gray-300" : "text-gray-400"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {!loading && grants.length === 0 && (
        <p className="mt-4 text-sm text-gray-500">
          {matchingMode === "allGrants"
            ? "No grants found on ALLGRANTS opportunities for this company. Try InterestedIn, or link GrantID on opportunity rows."
            : "No interested grants yet. Add grants to Interested In, or switch to All Grants."}
        </p>
      )}

      {grants.length > 0 && (
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12 min-w-0">
          {/* Grant tabs */}
          <nav
            className="lg:col-span-4 xl:col-span-3 min-w-0"
            aria-label="Grants"
          >
            <ul className="space-y-1 rounded-xl border border-gray-100 bg-white p-1.5">
              {grantTabs.map(({ grant, total, answered, unanswered, matchCount }) => {
                const isActive = !isSearching && grant.id === selectedGrantId;
                const hasSearchMatch = !isSearching || matchCount > 0;
                const opportunityCount = opportunityCountsByGrantId[grant.id] || 0;
                return (
                  <li key={grant.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedGrantId(grant.id);
                        if (isSearching) {
                          const el = document.getElementById(`grant-search-${grant.id}`);
                          el?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                      disabled={isSearching && matchCount === 0}
                      className={`w-full rounded-lg px-3 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        isActive
                          ? "bg-gray-900 text-white"
                          : hasSearchMatch
                            ? "text-gray-700 hover:bg-gray-50"
                            : "text-gray-400"
                      }`}
                    >
                      <span className="block text-sm font-medium truncate">
                        {grant.fields.Name}
                      </span>
                      <span
                        className={`mt-0.5 block text-xs ${
                          isActive ? "text-gray-300" : "text-gray-500"
                        }`}
                      >
                        {isSearching ? (
                          <>
                            {matchCount} match{matchCount === 1 ? "" : "es"}
                            {" · "}
                            {total} total
                          </>
                        ) : (
                          <>
                            {total} question{total === 1 ? "" : "s"}
                            {matchingMode === "allGrants" && opportunityCount > 0 && (
                              <>
                                {" · "}
                                {opportunityCount} opportunit
                                {opportunityCount === 1 ? "y" : "ies"}
                              </>
                            )}
                            {total > 0 && (
                              <>
                                {" · "}
                                <span className={isActive ? "text-emerald-300" : "text-emerald-600"}>
                                  {answered} answered
                                </span>
                                {unanswered > 0 && (
                                  <>
                                    {" · "}
                                    <span className={isActive ? "text-amber-200" : "text-amber-600"}>
                                      {unanswered} open
                                    </span>
                                  </>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Questions panel — cross-grant search, or single selected grant */}
          <div className="lg:col-span-8 xl:col-span-9 min-w-0">
            {isSearching ? (
              <div className="space-y-4 min-w-0">
                {crossGrantSearchResults.length === 0 ? (
                  <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-500">
                    No questions or answers match “{searchQuery}”.
                  </div>
                ) : (
                  crossGrantSearchResults.map(({ grant, questions }) => (
                    <div
                      key={grant.id}
                      id={`grant-search-${grant.id}`}
                      className="scroll-mt-24 rounded-xl border border-gray-100 bg-white p-4 sm:p-6 min-w-0"
                    >
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {grant.fields.Name}
                        </h4>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {questions.length} match{questions.length === 1 ? "" : "es"}
                        </p>
                      </div>
                      <ul className="divide-y divide-gray-100">
                        {questions.map((question) => renderQuestionRow(grant, question))}
                      </ul>
                    </div>
                  ))
                )}
              </div>
            ) : selectedGrant ? (
              <div className="rounded-xl border border-gray-100 bg-white p-4 sm:p-6 min-w-0">
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {selectedGrant.fields.Name}
                  </h4>
                  {selectedGrant.fields.GrantUrl && (
                    <a
                      href={selectedGrant.fields.GrantUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs font-medium text-gray-500 hover:text-gray-900 transition-colors"
                    >
                      Open grant details
                    </a>
                  )}
                </div>

                {selectedQuestions.length === 0 ? (
                  <p className="text-sm text-gray-500">No questions for this grant.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {selectedQuestions.map((question) =>
                      renderQuestionRow(selectedGrant, question)
                    )}
                  </ul>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-100 bg-white p-6 text-sm text-gray-500">
                Select a grant to view questions.
              </div>
            )}
          </div>
        </div>
      )}

      <Notification
        showNotif={showSubmitNotif.showNotif}
        setShowNotif={(val) => setShowNotif(val, setShowSubmitNotif)}
        contents={showSubmitNotif.contents}
      />
    </div>
  );
};

export default CompanyGrantQuestionsSection;
